import {NumberInput, Slider} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {suffixTestId, type Nullish} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {calculateSliderTicks} from '../../utils/calculateSliderTicks';
import {formatSliderValue} from '../../utils/formatSliderValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';
import {useRangeSliderFilter} from './hooks/useRangeSliderFilter';

type NumberSliderRangeProps = EagFilterProps & {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  unit: string | null;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  decimalsAllowed: boolean;
  inputsVisible: boolean;
  quickFilters?: Array<{value: string; label: string}>;
};

export function NumberSliderRangeFilter(props: NumberSliderRangeProps) {
  const ticks = calculateSliderTicks(props.min, props.max, props.markerSize, props.markers);

  const model = props.value[FILTER_VALUE_KEY] as [number | null, number | null] | string | Nullish;

  const step = props.stepSize ?? 1;

  const filter = useRangeSliderFilter({
    model,
    onModelChange: (newValue) => props.onChange({[FILTER_VALUE_KEY]: newValue}),
    min: props.min,
    max: props.max,
    step,
  });

  return (
    <VStack spacing={5}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={filter.quickFilterValue}
        defaultValue={null}
        setFilterValue={filter.applyQuickFilter}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('number-range-filter-quickFilters', props)}
      />
      <Slider
        isDisabled={props.isDisabled}
        isRange
        min={props.min}
        max={props.max}
        step={props.stepSize ?? (props.steps?.length ? props.steps : 1)}
        value={filter.sliderValue}
        allowMoveStart
        onChange={filter.onSliderChange}
        formatValue={(value) => formatSliderValue(value, props.unit)}
        ticks={ticks}
        onlyEdges={true}
        displayValue="auto"
        data-testid={suffixTestId('number-range-filter-slider', props)}
      />
      <HStack spacing={2}>
        {/* minStepperValue is OMITTED while a bound is empty so the platform
            stepper emits ±step artifacts — the only signal that lets
            useRangeSliderFilter tell plus apart from minus (empty From − →
            min, empty To + → max). Once a value exists it is passed again so
            the platform clamps at min and the displayed value cannot drift
            below it (the hook swallows the resulting no-op onChange). */}
        <NumberInput
          value={filter.fromValue}
          onChange={filter.onFromChange}
          isStepperVisible
          step={step}
          minStepperValue={filter.fromValue !== null ? props.min : undefined}
          maxStepperValue={props.max}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('number-range-filter-from', props)}
        />
        <NumberInput
          value={filter.toValue}
          onChange={filter.onToChange}
          isStepperVisible
          step={step}
          minStepperValue={filter.toValue !== null ? props.min : undefined}
          maxStepperValue={props.max}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('number-range-filter-to', props)}
        />
      </HStack>
    </VStack>
  );
}
